import { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';

import { resolveMediaUrl } from '@/utils/firebaseUtils';
import { logger } from '@/services/loggerService';
import { SecondBrainContextNote } from '../types';
import { formatNoteDate } from '../utils/relativeTime';
import { smallTagChipSx } from './tagChipStyles';

interface SourceNoteCardProps {
  note: SecondBrainContextNote;
  onOpen: (noteId: string) => void;
}

export const SourceNoteCard = ({ note, onOpen }: SourceNoteCardProps) => {
  const noteDate = formatNoteDate(note.createdAt);
  const [resolvedThumbnail, setResolvedThumbnail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!note.thumbnailUrl) {
      setResolvedThumbnail(null);
      return undefined;
    }

    resolveMediaUrl(note.thumbnailUrl)
      .then((secureUrl) => {
        if (!cancelled) setResolvedThumbnail(secureUrl);
      })
      .catch((err) => {
        logger.debug('secondBrain', 'Failed to resolve source note thumbnail, using placeholder', err);
        if (!cancelled) setResolvedThumbnail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [note.thumbnailUrl]);

  return (
    <Box
      onClick={() => onOpen(note.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        p: 1.25,
        border: '1px solid',
        borderColor: 'neutral.otherBorder',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'border-color 150ms ease, background-color 150ms ease',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
      }}
    >
      <Box
        sx={{
          flex: 'none',
          width: 84,
          height: 56,
          borderRadius: '6px',
          bgcolor: 'grey.200',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundImage: resolvedThumbnail ? `url(${resolvedThumbnail})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!resolvedThumbnail && <DescriptionOutlinedIcon sx={{ fontSize: 24, color: 'grey.500' }} />}
      </Box>
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
        <Typography variant="body2" fontWeight={500} color="text.primary" noWrap>
          {note.title || 'Untitled note'}
        </Typography>
        {noteDate && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {noteDate}
          </Typography>
        )}
        {Boolean(note.tags?.length) && (
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ overflow: 'hidden' }}>
            {note.tags?.slice(0, 3).map((tag, tagIndex) => (
              <Chip key={tag} label={tag} size="small" sx={smallTagChipSx(tagIndex)} />
            ))}
          </Stack>
        )}
      </Stack>
      <IconButton
        size="small"
        aria-label="Open note"
        onClick={(event) => {
          event.stopPropagation();
          onOpen(note.id);
        }}
      >
        <NorthEastIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default SourceNoteCard;
